import { useCallback, useState } from 'react';
import { DateString } from '../../../../../common/src/date_string.ts';
import {
  IntervalSongList,
  IntervalType,
  isDeletableSongListType,
  Org,
  OrgType,
  SongList,
  SongListEntryType,
  SongListSchema,
  SongListSchemaEntry,
  SongListType,
} from '../../../../../common/src/model.ts';
import { hasInterval } from '../../../../../common/src/model_util.ts';
import { LocalStorageKey } from '../../../data/client_local_storage.ts';
import { recordSongListUpdate, songListUpdatesPending } from '../../../data/song_updates.ts';
import {
  addSongListSyncListener,
  removeSongListSyncListener,
  synchronizeAllSongLists,
} from '../../../data/use_current_org.tsx';
import { useLocalStorage } from '../../../data/use_local_storage.tsx';
import { useEffectOnce } from '../../../util/use_effect_once.ts';
import { shiftSongListRows } from './song_list_rows.ts';

export interface AllSongLists {
  addCustomInterval({ start, end }: { start: DateString; end: DateString }): Promise<number>;
  deleteRowAt(rowIndex: number): Promise<void>;
  find(callback: (songList: SongList) => boolean): SongList | undefined;
  filter(callback: (songList: SongList) => boolean): SongList[];
  insertNewRowAt(rowIndex: number): Promise<void>;
  map<T>(callback: (songList: SongList) => T): T[];
  remove(songList: SongList): Promise<void>;
  update(songList: SongList): Promise<number>;
  updateEach(songList: SongList[]): Promise<void>;
  updateRowIndices({
    rowIndex,
    offset,
    intervalTypes,
  }: {
    rowIndex: number;
    offset: number;
    intervalTypes?: IntervalType[];
  }): Promise<void>;
  containsUpdates: boolean;
}

const defaultTermlyCount = 1;
const defaultMonthlyCount = 2;
const defaultWeeklyCount = 5;

const defaultSchoolSongListSchema: SongListSchema = {
  rows: [
    ...createSchemaEntries({ count: defaultTermlyCount, offset: 0, intervalType: IntervalType.Custom }),
    ...createSchemaEntries({
      count: defaultMonthlyCount,
      offset: defaultTermlyCount,
      intervalType: IntervalType.Month,
    }),
    ...createSchemaEntries({
      count: defaultWeeklyCount,
      offset: defaultTermlyCount + defaultMonthlyCount,
      intervalType: IntervalType.Week,
    }),
  ],
};

export function getSchoolSongListSchema(school: Org | undefined, allSongLists: AllSongLists): SongListSchema {
  if (school?.type !== OrgType.School) {
    throw new Error(`Only schools may be passed to getSchoolSongListSchema`);
  }
  const schemaHasIntervalRows =
    (school?.churchLiturgySchema?.rows ?? []).filter(({ intervalType }) => intervalType !== undefined).length > 0;
  if (schemaHasIntervalRows && school.churchLiturgySchema) {
    return school.churchLiturgySchema;
  }
  const maxWeeklyIntervalRowIndex = Math.max(
    0,
    ...allSongLists
      .filter((songList) => hasInterval(songList) && songList.interval.type === IntervalType.Week)
      .map(({ songs }) => Math.max(0, ...songs.map(({ row }) => row))),
  );
  return maxWeeklyIntervalRowIndex > 0
    ? { rows: createSchemaEntries({ count: maxWeeklyIntervalRowIndex, offset: 0, intervalType: IntervalType.Week }) }
    : defaultSchoolSongListSchema;
}

function createSchemaEntries({
  count,
  offset,
  intervalType,
}: {
  count: number;
  offset: number;
  intervalType: IntervalType;
}): SongListSchemaEntry[] {
  return [...Array(count).keys()].map((index) => ({
    row: index + offset + 1,
    type: SongListEntryType.Song,
    intervalType,
    label: '',
  }));
}

// cannot useState because it lags behind when adding multiple song lists, so forced to use global (yuck!)
let nextSongListId = -1;

export function useAllSongLists(): AllSongLists {
  const [allSongLists, setAllSongLists] = useLocalStorage(LocalStorageKey.AllSongLists);
  const [containsUpdates, setContainsUpdates] = useState<boolean>(false);

  const markSongListUpdated = useCallback(async (songList: SongList, { isDeleted }: { isDeleted?: boolean } = {}) => {
    await recordSongListUpdate(songList, { isDeleted });
    void synchronizeAllSongLists();
  }, []);

  const allocateNewSongListId = useCallback((): number => {
    return nextSongListId--;
  }, []);

  useEffectOnce(() => {
    const listener = async () => setContainsUpdates(await songListUpdatesPending());
    addSongListSyncListener(listener);
    return () => removeSongListSyncListener(listener);
  });

  const updateRowIndices = useCallback(
    async ({
      rowIndex,
      offset,
      intervalTypes,
    }: {
      rowIndex: number;
      offset: number;
      intervalTypes?: IntervalType[];
    }) => {
      const updatedSongLists: SongList[] = [];
      setAllSongLists((allSongLists) =>
        allSongLists.map((current) => {
          const newSongList = shiftSongListRows(current, { rowIndex, offset, intervalTypes });
          if (!newSongList) {
            return current;
          }
          if (current.songs.find(({ row }) => row >= rowIndex)) {
            updatedSongLists.push(newSongList);
          }
          return newSongList;
        }),
      );
      await Promise.all(
        updatedSongLists.map(async (songList) => {
          await recordSongListUpdate(songList);
        }),
      );
      void synchronizeAllSongLists();
    },
    [setAllSongLists],
  );

  const deleteRowAt = useCallback(
    async (rowIndex: number) => {
      await updateRowIndices({ rowIndex, offset: -1 });
    },
    [updateRowIndices],
  );

  const find = useCallback(
    (callback: (songList: SongList) => boolean) => {
      return allSongLists.find(callback);
    },
    [allSongLists],
  );

  const filter = useCallback(
    (callback: (songList: SongList) => boolean) => {
      return allSongLists.filter(callback);
    },
    [allSongLists],
  );

  const insertNewRowAt = useCallback(
    async (rowIndex: number) => {
      await updateRowIndices({ rowIndex, offset: 1 });
    },
    [updateRowIndices],
  );

  const map = useCallback(
    (callback: (songList: SongList) => any) => {
      return allSongLists.map(callback);
    },
    [allSongLists],
  );

  const remove = useCallback(
    async (songList: SongList) => {
      if (!isDeletableSongListType(songList?.type)) {
        // only allow events to be removed
        return;
      }
      setAllSongLists((allSongLists) => allSongLists.filter((current) => current.id !== songList.id));
      await markSongListUpdated(songList, { isDeleted: true });
    },
    [setAllSongLists, markSongListUpdated],
  );

  const update = useCallback(
    async (songList: SongList) => {
      songList.id = songList.id ?? allocateNewSongListId();
      setAllSongLists((allSongLists) => [...allSongLists.filter((list) => list.id !== songList.id), songList]);
      await markSongListUpdated(songList);
      return songList.id;
    },
    [allocateNewSongListId, markSongListUpdated, setAllSongLists],
  );

  const updateEach = useCallback(
    async (songLists: SongList[]) => {
      for (const songList of songLists) {
        songList.id = songList.id ?? allocateNewSongListId();
        setAllSongLists((allSongLists) => [...allSongLists.filter((list) => list.id !== songList.id), songList]);
        await recordSongListUpdate(songList);
      }
      void synchronizeAllSongLists();
    },
    [allocateNewSongListId, setAllSongLists],
  );

  const addCustomInterval = useCallback(
    async ({ start, end }: { start: DateString; end: DateString }): Promise<number> => {
      const songList: IntervalSongList & { id: number } = {
        id: allocateNewSongListId(),
        type: SongListType.Interval,
        interval: {
          type: IntervalType.Custom,
          start,
          end,
        },
        songs: [],
      };
      setAllSongLists((allSongLists) => [...allSongLists, songList]);
      await markSongListUpdated(songList);
      return songList.id;
    },
    [allocateNewSongListId, markSongListUpdated, setAllSongLists],
  );

  return {
    addCustomInterval,
    containsUpdates,
    deleteRowAt,
    find,
    filter,
    insertNewRowAt,
    map,
    remove,
    update,
    updateEach,
    updateRowIndices,
  };
}
